import {scalar} from "./scalar";

const clamp = (a: number, min: number, max: number) => {
    return Math.max(Math.min(a, max), min);
}
export const v3 = ({
    eps: 1e-6,
    lenSq: (v: number[]) => {
        return v[0] * v[0] + v[1] * v[1] + v[2] * v[2];
    },
    length: (v: number[]) => {
        return Math.sqrt(v3.lenSq(v))
    },
    normalize: (v: number[]) => {
        const len = v3.length(v) || 1
        return v3.scale(v, 1.0 / len);
    },
    addVectors: (a: number[], b: number[]) => {
        return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
    },
    subVectors: (a: number[], b: number[]) => {
        return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
    },
    negate: (a: number[]) => {
        return a.map(v => -v);
    },
    crossVectors: (a: number[], b: number[]) => {
        const ax = a[0], ay = a[1], az = a[2];
        const bx = b[0], by = b[1], bz = b[2];
        return [
            ay * bz - az * by,
            az * bx - ax * bz,
            ax * by - ay * bx
        ]
    },
    addScaledVector: (a: number[], v: number[], scalar: number) => {
        return [
            a[0] + (v[0] * scalar),
            a[1] + (v[1] * scalar),
            a[2] + (v[2] * scalar),
        ]
    },
    dotVectors: (a: number[], b: number[]) => {
        return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
    },
    multiplyScalar: (a: number[], scalar: number) => {
        return [
            a[0] * scalar,
            a[1] * scalar,
            a[2] * scalar
        ]
    },
    projectOnVector: (a: number[], b: number[]) => {
        const denominator = v3.lenSq(b);
        if (Math.abs(denominator) < Number.EPSILON) return [0, 0, 0];

        const scalar = v3.dotVectors(a, b) / denominator;
        return v3.multiplyScalar(b, scalar);
    },
    projectOnPlane: (a: number[], planeNormal: number[]) => {
        const tmp = v3.projectOnVector(a, planeNormal)
        return v3.subVectors(a, tmp)
    },
    equals: (a: number[], b: number[]) => {
        return a.every((v, idx) => {
            return Math.abs(v - (b[idx] || 0)) < v3.eps;
        })
    },
    angleTo: (a: number[], b: number[]) => {
        const denominator = Math.sqrt(v3.lenSq(a) * v3.lenSq(b));

        if (denominator < v3.eps) return Math.PI / 2;

        const theta = v3.dotVectors(a, b) / denominator;

        // clamp, to handle numerical problems

        return Math.acos(clamp(theta, -1, 1));
    },
    angleBetweenVectors: (vec0: number[], vec1: number[], norm: number[]) => {
        const v0 = v3.normalize(vec0);
        const v1 = v3.normalize(vec1);
        const dot = scalar.clamp(v3.dotVectors(v0, v1), -1, 1);
        const angle = Math.acos(dot);
        const n = v3.crossVectors(v0, v1);
        if (v3.dotVectors(n, norm) > 0) {
            return angle;
        }
        return -Math.acos(dot);
    },
    randomDirection: () => {
        // Derived from https://mathworld.wolfram.com/SpherePointPicking.html
        const u = (Math.random() - 0.5) * 2;
        const t = Math.random() * Math.PI * 2;
        const f = Math.sqrt(1 - u ** 2);

        return [
            f * Math.cos(t),
            f * Math.sin(t),
            u
        ]
    },
    distance: (a: number[], b: number[]) => v3.length(v3.subVectors(a, b)),
    lerp: (start: number[], end: number[], t: number) => {
        return v3.addVectors(v3.multiplyScalar(start, 1.0 - t), v3.multiplyScalar(end, t));
    },
    midpoint: (start: number[], end: number[]) => {
        return v3.addScaledVector(start, v3.subVectors(end, start), 0.5);
    },
    scale: (v: number[], scale: number) => {
        return v.map(x => x * scale);
    },
    directionTo: (start: number[], end: number[]) => {
        return v3.normalize(v3.subVectors(end, start));
    },
    rotate: (rotation: number, v: number[], axisOfRotation = [0, 0, 1]) => {
        const axis = v3.normalize(axisOfRotation);
        const cosRot = Math.cos(rotation)
        const sinRot = Math.sin(rotation)

        const p1 = v3.multiplyScalar(v, cosRot);
        const axisXv = v3.crossVectors(axis, v)
        const p2 = v3.multiplyScalar(axisXv, sinRot);
        const axisDotV = v3.dotVectors(axis, v)
        const p3Norm = v3.multiplyScalar(axis, axisDotV)
        const p3 = v3.multiplyScalar(p3Norm, (1 - cosRot));

        const p1p2 = v3.addVectors(p1, p2)

        return v3.addVectors(p1p2, p3);
    },
    centroid: (vectors: number[][]) => {
        const addedVector = vectors.reduce((acc, v) => v3.addVectors(acc, v), [0, 0, 0]);

        return v3.multiplyScalar(addedVector, 1 / vectors.length);
    },
    minDims: (vectors: number[][]) => {
        return vectors.reduce((acc, v) => {
            if (v[0] < acc[0]) acc[0] = v[0]
            if (v[1] < acc[1]) acc[1] = v[1]
            if (v[2] < acc[2]) acc[2] = v[2]
            return acc;
        }, [Infinity, Infinity, Infinity])
    },
    maxDims: (vectors: number[][]) => {
        return vectors.reduce((acc, v) => {
            if (v[0] > acc[0]) acc[0] = v[0]
            if (v[1] > acc[1]) acc[1] = v[1]
            if (v[2] > acc[2]) acc[2] = v[2]
            return acc;
        }, [-Infinity, -Infinity, -Infinity])
    },
    fromXYZObj: (xyz: { x: number, y: number, z?: number | null }) => [xyz.x, xyz.y, xyz.z || 0],
    toXYZObj: (pos: number[]) => ({x: pos[0], y: pos[1], z: pos[2]}),
    applyQuaternion: (v: number[], q: number[]) => {
        // quaternion q is assumed to have unit length

        const vx = v[0], vy = v[1], vz = v[2];
        const qx = q[0], qy = q[1], qz = q[2], qw = q[3];

        // t = 2 * cross( q.xyz, v );
        const tx = 2 * (qy * vz - qz * vy);
        const ty = 2 * (qz * vx - qx * vz);
        const tz = 2 * (qx * vy - qy * vx);

        // v + q.w * t + cross( q.xyz, t );
        return [
            vx + qw * tx + qy * tz - qz * ty,
            vy + qw * ty + qz * tx - qx * tz,
            vz + qw * tz + qx * ty - qy * tx,
        ]
    }
})
